import { useContractWrite, usePrepareContractWrite, useNetwork, useWaitForTransaction } from 'wagmi';
import { getDexContracts } from '@/config/dex-v3-config';
import { pancakeswapV3PositionManagerABI } from '@/config/abis/pancakeswapV3PositionManager';
import { useToast } from '@/components/ui/use-toast';
import { parseEther } from 'viem';

interface AddLiquidityParams {
  token0: string;
  token1: string;
  feeTier: number;
  amount0: string; // In ether units
  amount1: string; // In ether units
  recipient: string;
  slippageTolerance?: number; // Default 0.5%
}

/**
 * Hook to add liquidity to a PancakeSwap V3 pool
 * Creates a full-range position for simplicity
 */
export function useAddLiquidity(params: AddLiquidityParams | null) {
  const { chain } = useNetwork();
  const { toast } = useToast();
  const contracts = chain?.id ? getDexContracts(chain.id) : null;

  const slippage = params?.slippageTolerance || 0.5; // 0.5% default
  const slippageMultiplier = 1 - slippage / 100;

  // Sort tokens to ensure correct order
  const sortedTokens = params && params.token0 && params.token1 && params.token0.toLowerCase() < params.token1.toLowerCase()
    ? { token0: params.token0, token1: params.token1, amount0: params.amount0, amount1: params.amount1 }
    : params && params.token0 && params.token1
    ? { token0: params.token1, token1: params.token0, amount0: params.amount1, amount1: params.amount0 }
    : null;

  // Full range ticks for V3
  const TICK_LOWER = -887220;
  const TICK_UPPER = 887220;

  // Calculate minimum amounts with slippage
  const amount0Min = params?.amount0 
    ? BigInt(Math.floor(Number(parseEther(params.amount0)) * slippageMultiplier))
    : 0n;
  const amount1Min = params?.amount1
    ? BigInt(Math.floor(Number(parseEther(params.amount1)) * slippageMultiplier))
    : 0n;

  // Deadline: 20 minutes from now
  const deadline = BigInt(Math.floor(Date.now() / 1000) + 1200);

  const mintParams = params && sortedTokens ? {
    token0: sortedTokens.token0 as `0x${string}`,
    token1: sortedTokens.token1 as `0x${string}`,
    fee: params.feeTier,
    tickLower: TICK_LOWER,
    tickUpper: TICK_UPPER,
    amount0Desired: parseEther(sortedTokens.amount0),
    amount1Desired: parseEther(sortedTokens.amount1),
    amount0Min,
    amount1Min,
    recipient: params.recipient as `0x${string}`,
    deadline,
  } : undefined;

  const { config } = usePrepareContractWrite({
    address: contracts?.positionManager as `0x${string}`,
    abi: pancakeswapV3PositionManagerABI,
    functionName: 'mint',
    args: mintParams ? [mintParams] : undefined,
    enabled: Boolean(contracts?.positionManager && mintParams),
  });

  const { write, data, isLoading: isWriting } = useContractWrite({
    ...config,
    onSuccess: () => {
      toast({
        title: 'Transaction Submitted',
        description: 'Adding liquidity to the pool...',
      });
    },
    onError: (error) => {
      toast({
        title: 'Add Liquidity Failed',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const { isLoading: isConfirming, isSuccess } = useWaitForTransaction({
    hash: data?.hash,
    onSuccess: () => {
      toast({
        title: 'Liquidity Added!',
        description: 'Your liquidity has been added successfully. You received an NFT position.',
      });
    },
  });

  const addLiquidity = () => {
    if (write) {
      write();
    }
  };

  return {
    addLiquidity,
    isAdding: isWriting || isConfirming,
    isSuccess,
    isSupported: Boolean(contracts?.positionManager),
    canAdd: Boolean(write),
  };
}
